import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Target,
  TrendingUp,
  Users,
  BarChart3,
  Activity,
  Award,
  AlertCircle,
  Eye,
  Download
} from 'lucide-react';
import { 
  VictoryChart, 
  VictoryPolarAxis,
  VictoryArea,
  VictoryTheme,
  VictoryLegend,
  VictoryBar,
  VictoryAxis
} from 'victory';

const API = process.env.REACT_APP_BACKEND_URL;

const Dashboard360 = () => {
  const { cicloId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState(null);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  const [ciclo, setCiclo] = useState(null);
  const [dadosGraficos, setDadosGraficos] = useState(null);

  useEffect(() => {
    if (cicloId) {
      carregarDashboard();
    }
  }, [cicloId]);

  const carregarDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Buscar dados do dashboard
      const dashResponse = await axios.get(`${API}/api/feedback360/dashboard/${cicloId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCiclo(dashResponse.data.ciclo);
      
      // Buscar resultados detalhados
      const resultResponse = await axios.get(`${API}/api/feedback360/resultados/${cicloId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResultados(resultResponse.data);
      
      // Selecionar primeiro funcionário por padrão
      if (resultResponse.data.funcionarios && resultResponse.data.funcionarios.length > 0) {
        setFuncionarioSelecionado(resultResponse.data.funcionarios[0]);
      }
      
      // Buscar dados para gráficos adicionais
      const graficosResponse = await axios.get(`${API}/api/feedback360/dashboard/${cicloId}/graficos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDadosGraficos(graficosResponse.data.graficos);
      
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dashboard 360º');
    } finally {
      setLoading(false);
    }
  };

  const prepararDadosGraficoRadar = () => {
    if (!funcionarioSelecionado || !funcionarioSelecionado.competencias) {
      return [];
    }

    const competencias = Object.keys(funcionarioSelecionado.competencias);
    
    // Preparar dados para cada tipo de avaliador
    const datasets = [
      {
        name: 'Auto-avaliação',
        data: competencias.map(comp => ({
          x: comp.replace('_', ' '),
          y: funcionarioSelecionado.competencias[comp].autoavaliacao || 0
        })),
        color: '#3b82f6'
      },
      {
        name: 'Gestor',
        data: competencias.map(comp => ({
          x: comp.replace('_', ' '),
          y: funcionarioSelecionado.competencias[comp].gestor || 0
        })),
        color: '#10b981'
      },
      {
        name: 'Pares',
        data: competencias.map(comp => ({
          x: comp.replace('_', ' '),
          y: funcionarioSelecionado.competencias[comp].pares || 0
        })),
        color: '#f59e0b'
      },
      {
        name: 'Clientes',
        data: competencias.map(comp => ({
          x: comp.replace('_', ' '),
          y: funcionarioSelecionado.competencias[comp].clientes_internos || 0
        })),
        color: '#8b5cf6'
      }
    ];

    return datasets;
  };

  const exportarPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback360/relatorios/pdf?ciclo_id=${cicloId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback360_${cicloId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const exportarExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback360/relatorios/excel?ciclo_id=${cicloId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback360_${cicloId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar Excel');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const dadosRadar = prepararDadosGraficoRadar();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Target className="h-8 w-8 mr-3 text-purple-600" />
            Dashboard 360º
          </h1>
          {ciclo && (
            <p className="text-gray-600 mt-1">{ciclo.nome}</p>
          )}
        </div>
        
        <div className="flex space-x-2">
          <Button onClick={exportarPDF} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button onClick={exportarExcel} className="bg-purple-600 hover:bg-purple-700">
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Estatísticas Gerais */}
      {resultados && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Funcionários</p>
                  <p className="text-3xl font-bold text-blue-600">{resultados.total_funcionarios}</p>
                </div>
                <Users className="h-10 w-10 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avaliações Completas</p>
                  <p className="text-3xl font-bold text-green-600">
                    {resultados.funcionarios.filter(f => f.total_avaliadores.auto > 0 && f.total_avaliadores.gestor > 0).length}
                  </p>
                </div>
                <Activity className="h-10 w-10 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Média Geral</p>
                  <p className="text-3xl font-bold text-purple-600">4.2</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taxa Participação</p>
                  <p className="text-3xl font-bold text-orange-600">85%</p>
                </div>
                <Award className="h-10 w-10 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Seletor de Funcionário */}
      {resultados && resultados.funcionarios.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Selecione um Funcionário</CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={funcionarioSelecionado?.funcionario_id || ''}
              onChange={(e) => {
                const func = resultados.funcionarios.find(f => f.funcionario_id === e.target.value);
                setFuncionarioSelecionado(func);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {resultados.funcionarios.map(func => (
                <option key={func.funcionario_id} value={func.funcionario_id}>
                  Funcionário {func.funcionario_id.substring(0, 8)}...
                </option>
              ))}
            </select>
          </CardContent>
        </Card>
      )}

      {/* Gráfico Radar 360º */}
      {funcionarioSelecionado && dadosRadar.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Visão 360º - Todas as Perspectivas
            </CardTitle>
            <CardDescription>
              Comparação entre auto-avaliação, gestor, pares e clientes internos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <svg viewBox="0 0 450 450" width="450" height="450">
                <VictoryChart
                  polar
                  theme={VictoryTheme.material}
                  domain={{ y: [0, 5] }}
                  width={450}
                  height={450}
                  standalone={false}
                >
                  <VictoryLegend
                    x={50}
                    y={10}
                    orientation="horizontal"
                    gutter={20}
                    style={{ labels: { fontSize: 10 } }}
                    data={dadosRadar.map(d => ({ name: d.name, symbol: { fill: d.color } }))}
                  />
                  
                  <VictoryPolarAxis
                    dependentAxis
                    style={{
                      axis: { stroke: 'none' },
                      grid: { stroke: '#e5e7eb', strokeWidth: 1 }
                    }}
                    tickValues={[1, 2, 3, 4, 5]}
                  />
                  
                  <VictoryPolarAxis
                    style={{
                      axis: { stroke: 'none' },
                      tickLabels: { fontSize: 9, padding: 10 }
                    }}
                  />
                  
                  {dadosRadar.map((dataset, index) => (
                    <VictoryArea
                      key={index}
                      data={dataset.data}
                      style={{
                        data: { 
                          fill: dataset.color,
                          fillOpacity: 0.3,
                          stroke: dataset.color,
                          strokeWidth: 2
                        }
                      }}
                    />
                  ))}
                </VictoryChart>
              </svg>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela de Notas Consolidadas */}
      {funcionarioSelecionado && (
        <Card>
          <CardHeader>
            <CardTitle>Notas Consolidadas por Competência</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Competência</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Auto</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Gestor</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Pares</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Clientes</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">360º</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Consistência</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(funcionarioSelecionado.competencias).map(([comp, dados]) => (
                    <tr key={comp} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                        {comp.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-blue-600 font-medium">
                        {dados.autoavaliacao}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-green-600 font-medium">
                        {dados.gestor}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-orange-600 font-medium">
                        {dados.pares}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-purple-600 font-medium">
                        {dados.clientes_internos}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-bold text-gray-900">
                        {dados.media_360}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {dados.consistencia === 'alta' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                            Alta
                          </span>
                        )}
                        {dados.consistencia === 'media' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">
                            Média
                          </span>
                        )}
                        {dados.consistencia === 'baixa' && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                            Baixa
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gráficos Adicionais */}
      {dadosGraficos && (
        <>
          {/* Comparativo por Tipo de Avaliador */}
          {dadosGraficos.comparativo_tipos && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Comparativo por Tipo de Avaliador
                </CardTitle>
                <CardDescription>
                  Média de notas por perspectiva (auto, gestor, pares, clientes)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <VictoryChart
                  theme={VictoryTheme.material}
                  domainPadding={30}
                  height={250}
                >
                  <VictoryAxis
                    tickFormat={Object.keys(dadosGraficos.comparativo_tipos).map(t => 
                      t.replace('_', ' ').replace('autoavaliacao', 'Auto').replace('cliente_interno', 'Cliente').replace('par', 'Par').replace('gestor', 'Gestor').replace('subordinado', 'Sub')
                    )}
                    style={{
                      tickLabels: { fontSize: 10, angle: -20, textAnchor: 'end' }
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    domain={[0, 5]}
                  />
                  <VictoryBar
                    data={Object.entries(dadosGraficos.comparativo_tipos).map(([tipo, dados]) => ({
                      x: tipo.replace('_', ' ').substring(0, 8),
                      y: dados.media,
                      label: dados.media.toFixed(1)
                    }))}
                    style={{
                      data: { fill: '#8b5cf6' }
                    }}
                    labels={({ datum }) => datum.label}
                  />
                </VictoryChart>
              </CardContent>
            </Card>
          )}

          {/* Ranking Top 5 Competências */}
          {dadosGraficos.top_5_competencias && dadosGraficos.top_5_competencias.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Award className="h-5 w-5 mr-2 text-yellow-600" />
                  Top 5 Competências Mais Bem Avaliadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dadosGraficos.top_5_competencias.map((comp, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900">{comp.competencia}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-green-600">{comp.media}</span>
                        <span className="text-xs text-gray-500">({comp.total_avaliacoes} avaliações)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bottom 5 - Áreas de Melhoria */}
          {dadosGraficos.bottom_5_competencias && dadosGraficos.bottom_5_competencias.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
                  Top 5 Áreas de Melhoria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dadosGraficos.bottom_5_competencias.map((comp, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900">{comp.competencia}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-bold text-orange-600">{comp.media}</span>
                        <span className="text-xs text-gray-500">({comp.total_avaliacoes} avaliações)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Estado vazio */}
      {!resultados && !loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">Nenhum resultado disponível</p>
              <p className="text-sm text-gray-500 mt-2">
                Aguarde as avaliações serem concluídas
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard360;